import { ICellRendererParams } from '@ag-grid-community/core';

import { AqdbBatch } from '../../../../aqdb/models/aqdb-batch';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import { CommonColDef, DateColDef, LinkColDef, MultiLineColDef } from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { CheckboxHeaderComponent } from '../../header-components/checkbox-header.component';
import { DatacenterDateRangeFilterComponent } from '../../filter-type/datacenter-date-range-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { formatNumber } from '../../../utils/number.format';
import { UNIT_OPTIONS } from '../../../models/list';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';

import { AqdbMechanicalCharacteristicsColumnDefinition } from './aqdb-mechanical-datacenter-configuration';
import { AqdbChemicalCharacteristicsColumnDefinition } from './aqdb-chemical-datacenter-configuration';
import {
	AqdbCoatingAndMagneticCharacteristicsColumnDefinition
} from './aqdb-coating-and-magnetic-datacenter-configuration';

const linkToAqdbBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<AqdbBatch, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const batch = params.data;
		return {
			commands: [
				'/aqdb',
				'card',
				batch.id,
			]
		};
	}
};

export class AqdbDatacenterColumnDefinition {
	readonly UNIT_OPTIONS = UNIT_OPTIONS;

	readonly AQDB_DATA_CENTER_DEFAULT_CONFIGURATION:
	Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>;

	constructor(private readonly translationService: TranslationService) {
		this.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION =
		new Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>(
			[
				[
					'batchReference',
					new LinkColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.batch'),
						field: 'batchReference',
						colId: 'batchReference',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: linkToAqdbBatchCardCellRendererParams,
						filter: DatacenterTextFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'serialNumber',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.serial.number'),
						field: 'serialNumber',
						colId: 'serialNumber',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'recipient',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.recipient'),
						field: 'recipientLabel',
						colId: 'recipient',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						sortable: false,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'customerArticleRef',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.customer.article.ref'),
						field: 'articleReference',
						colId: 'customerArticleRef',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'dispatchDate',
					new DateColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.dispatch.date'),
						field: 'shipmentDate',
						colId: 'dispatchDate',
						columnType: ColumnType.DATE_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { inputFormat: 'isoDatetime', outputFormat: 'date' },
						filter: DatacenterDateRangeFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'despatchAdviceNumber',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.N.of.dispatch.note'),
						field: 'despatchAdviceNumber',
						colId: 'despatchAdviceNumber',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'commercialQuality',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.commercial.quality'),
						field: 'commercialQualityLabel',
						colId: 'commercialQuality',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						sortable: false,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'deliveryMill',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.delivery.mill'),
						field: 'deliveryMillDescription',
						colId: 'deliveryMill',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterTextFilterComponent,
						filterParams: { filterOptions: ['contains'] },
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'form',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.form'),
						field: 'form',
						colId: 'form',
						columnType: ColumnType.TEXT_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						filter: DatacenterDropdownFilterComponent,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
						valueFormatter: params => {
							const code = String(params.value);
							return code ? this.translationService.getTR(`form.indif.${ code }`) : '';
						}
					})
				],
				[
					'grossWeight',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.gross.weight'),
						field: 'qtyBatchGross',
						colId: 'grossWeight',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'weight-with-zero' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
						valueFormatter: params => `${ formatNumber(
							Number(params.value),
							'weight'
						) } ${ this.UNIT_OPTIONS['to'].symbol }`
					})
				],
				[
					'netWeight',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.net.weight'),
						field: 'qtyBatchNet',
						colId: 'netWeight',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'weight-with-zero' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
						valueFormatter: params => `${ formatNumber(
							Number(params.value),
							'weight'
						) } ${ this.UNIT_OPTIONS['to'].symbol }`
					})
				],
				[
					'nominalThickness',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.nominal.thickness'),
						field: 'nominalThickness',
						colId: 'nominalThickness',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'thickness' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'nominalWidth',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.nominal.width'),
						field: 'nominalWidth',
						colId: 'nominalWidth',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'width' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'realThickness',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.thickness.mm'),
						field: 'realThickness',
						colId: 'realThickness',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'thickness' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'realWidth',
					new CommonColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.width.mm'),
						field: 'realWidth',
						colId: 'realWidth',
						columnType: ColumnType.NUMBER_COLUMN,
						width: 120,
						minWidth: 120,
						maxWidth: 120,
						resizable: true,
						cellRendererParams: { numericFormat: 'width' },
						filter: DatacenterNumberFilterComponent,
						sortable: true,
						headerComponent: CheckboxHeaderComponent,
						headerComponentParams: { showCheckbox: false },
						suppressHeaderMenuButton: false,
					})
				],
				[
					'markedIdentity',
					new MultiLineColDef<AqdbBatch>({
						headerName: this.translationService.getTR('aqdb.marked.identity'),
						fields: ['segments.markedIdentity'],
						colId: 'markedIdentity',
						columnType: ColumnType.TEXT_COLUMN,
						valueType: MultiLineValueTypes.TEXT,
						valueGetter: params => {
							const qualityBatch = params.data;
							return qualityBatch
								? qualityBatch.segments
									.map(item => item.markedIdentity).join(';') : '';
						},
					})
				]
			]
		);
		const mechanicalCharacteristics = new AqdbMechanicalCharacteristicsColumnDefinition(translationService);
		const chemicalCharacteristics = new AqdbChemicalCharacteristicsColumnDefinition(translationService);
		const coatingAndMagneticCharacteristics =
			new AqdbCoatingAndMagneticCharacteristicsColumnDefinition(translationService);

		this.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION =
			new Map<string, CommonColDef<AqdbBatch> | CommonColDef<AqdbBatch, string>>(
				[
					...this.AQDB_DATA_CENTER_DEFAULT_CONFIGURATION,
					...mechanicalCharacteristics.AQDB_MECHANICAL_CHARACTERISTICS_CONFIGURATION,
					...chemicalCharacteristics.AQDB_CHEMICAL_CHARACTERISTICS_CONFIGURATION,
					...coatingAndMagneticCharacteristics.AQDB_COATING_AND_MAGNETIC_CHARACTERISTICS_CONFIGURATION
				]
			);
	}
}

export const DEFAULT_VIEW_AQDB_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'batchReference'
		},
		{
			colId: 'serialNumber'
		},
		{
			colId: 'recipient'
		},
		{
			colId: 'customerArticleRef'
		},
		{
			colId: 'dispatchDate'
		},
		{
			colId: 'despatchAdviceNumber'
		},
		{
			colId: 'commercialQuality'
		},
		{
			colId: 'deliveryMill'
		},
		{
			colId: 'form'
		},
		{
			colId: 'grossWeight'
		},
		{
			colId: 'realThickness'
		},
		{
			colId: 'realWidth'
		}
	],
	sortModel: [
		{
			colId: 'dispatchDate',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'aqdb',
	isDefault: true,
	name: 'Default',
	id: -99
};
